import { notifications } from '@mantine/notifications';
import colors from 'common/constants/colors.styles';
import { Form } from 'components/elements';
import { FormProps } from 'components/elements/form';
import { useRouter } from 'next/router';
import React from 'react';
import {
  DefaultValues,
  FieldValues,
  UseFormProps,
  UseFormReturn,
  useForm,
} from 'react-hook-form';

export interface UniskaiFormProps<
  T extends FieldValues = FieldValues,
  TContext = any,
  TReturn = any,
> extends UseFormProps<T, TContext> {
  defaultValues?: DefaultValues<T>;
  children: React.ReactNode;
  onSubmit: (input: T, form: UseFormReturn<T, TContext>) => Promise<TReturn>;
  defaultEditable?: FormProps['defaultEditable'];
  customViewName?: FormProps['customViewName'];
  keyName?: FormProps['keyName'];
  noReset?: FormProps['noReset'];
  formRef?: React.ComponentProps<typeof Form>['ref'];
}

export default function UniskaiForm<T extends FieldValues = FieldValues>(
  props: UniskaiFormProps<T>,
) {
  const { asPath } = useRouter();
  const {
    children,
    defaultEditable,
    customViewName,
    keyName = asPath,
    noReset,
    formRef,
    ...methodsProps
  } = props;

  const methods = useForm(methodsProps);
  const onSubmit = React.useCallback(
    async (values: T) => {
      try {
        await props.onSubmit(values, methods);
      } catch (e) {
        console.error(e);
        notifications.show({
          message: e.message,
          color: colors.red,
        });
      }
    },
    [methods, props],
  );

  return (
    <Form
      ref={formRef}
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={defaultEditable}
      customViewName={customViewName}
      keyName={keyName}
      noReset={noReset}
      style={{
        display: 'block',
        height: '100%',
      }}
    >
      {children}
    </Form>
  );
}
